import classNames from 'classnames';
import {
  type ClipboardEvent,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type FocusEvent,
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useState,
} from 'react';

import { InputChip } from './InputChip';
import styles from './MultiChipInput.module.css';
import { splitPastedText } from './multi-chip-input-utils';

type Size = 'sm' | 'md' | 'lg';

export type MultiChipInputCssVar =
  | '--multi-chip-input-disabled-bg'
  | '--multi-chip-input-disabled-border'
  | '--multi-chip-input-disabled-color'
  | '--multi-chip-input-disabled-placeholder-color'
  | '--multi-chip-input-chip-disabled-bg'
  | '--multi-chip-input-chip-disabled-border'
  | '--multi-chip-input-chip-disabled-color';

export type ChipItem = {
  id: string;
  label: string;
};

export type MultiChipInputProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'children'
> & {
  items: ChipItem[];
  onRemove: (id: string) => void;
  size?: Size;
  isInvalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  renderChip?: (item: ChipItem, onRemove: () => void) => ReactNode;
  addSlot?: ReactNode;
  isEditable?: boolean;
  onAdd?: (values: string[]) => void;
  inputRef?: Ref<HTMLInputElement>;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onInputFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onInputBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onInputKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  cssVars?: Partial<Record<MultiChipInputCssVar, string>>;
};

export const MultiChipInput = forwardRef<HTMLDivElement, MultiChipInputProps>(
  (
    {
      items,
      onRemove,
      size = 'md',
      isInvalid,
      disabled,
      placeholder,
      className,
      onClick,
      renderChip,
      addSlot,
      isEditable = false,
      onAdd,
      inputRef,
      inputValue: controlledInputValue,
      onInputChange,
      onInputFocus,
      onInputBlur,
      onInputKeyDown,
      cssVars,
      style,
      ...rest
    },
    ref
  ) => {
    const [uncontrolledInputValue, setUncontrolledInputValue] = useState('');
    const [focused, setFocused] = useState(false);

    const isControlled = controlledInputValue !== undefined;
    const inputValue = isControlled
      ? controlledInputValue
      : uncontrolledInputValue;

    const setInputValue = (value: string) => {
      if (isControlled) {
        onInputChange?.(value);
      } else {
        setUncontrolledInputValue(value);
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      onInputKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key !== 'Enter' || e.repeat || disabled || !inputValue) return;
      e.preventDefault();
      onAdd?.([inputValue]);
      setInputValue('');
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('Text');
      const trimmed = splitPastedText(text);
      if (trimmed.length > 0) {
        onAdd?.(trimmed);
      }
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onInputFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onInputBlur?.(e);
    };

    const showPlaceholder =
      items.length === 0 && !isEditable && placeholder !== undefined;

    return (
      <div
        ref={ref}
        {...rest}
        className={classNames(styles.wrapper, styles[size], className)}
        style={{ ...style, ...(cssVars as CSSProperties) }}
        data-focused={focused || undefined}
        data-disabled={disabled || undefined}
        data-invalid={isInvalid || undefined}
        onClick={disabled ? undefined : onClick}
      >
        <div className={styles.control}>
          {items.map((item) => {
            const remove = () => onRemove(item.id);
            if (renderChip) {
              return <span key={item.id}>{renderChip(item, remove)}</span>;
            }
            return (
              <InputChip
                key={item.id}
                label={item.label}
                onRemove={remove}
                disabled={!!disabled}
                isInvalid={!!isInvalid}
              />
            );
          })}

          {isEditable && (
            <input
              ref={inputRef}
              className={styles.input}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              placeholder={items.length === 0 ? placeholder : undefined}
            />
          )}

          {showPlaceholder && (
            <span className={styles.placeholder}>{placeholder}</span>
          )}

          {!isEditable && addSlot !== undefined && (
            <span className={styles.addSlot}>{addSlot}</span>
          )}
        </div>
      </div>
    );
  }
);
